import { useEffect, useRef, useCallback } from "react"
import { consumer } from "@/lib/cable"
import { useAuthStore } from "@/stores/auth-store"
import { useAiStore } from "@/stores/ai-store"
import { useQueryClient } from "@tanstack/react-query"
import type { AiStreamMessage } from "@/lib/ai-types"

/**
 * Subscribes to AiChatChannel via ActionCable.
 * Handles streaming deltas, tool call progress, and completion.
 */
export function useAiStream() {
  const queryClient = useQueryClient()
  const { isAuthenticated } = useAuthStore()
  const {
    activeConversationId,
    setStreaming,
    appendStreamContent,
    resetStreamContent,
    addToolCall,
    completeToolCall,
  } = useAiStore()
  const subscriptionRef = useRef<ReturnType<typeof consumer.subscriptions.create> | null>(null)

  const handleReceived = useCallback(
    (data: AiStreamMessage) => {
      // Only process messages for the active conversation
      if (data.conversation_id !== activeConversationId) return

      switch (data.type) {
        case "delta":
          appendStreamContent(data.content)
          break

        case "tool_start":
          addToolCall(data.tool_call_id, data.tool_name)
          break

        case "tool_result":
          completeToolCall(data.tool_call_id)
          break

        case "done":
          setStreaming(false)
          // Invalidate conversation messages so React Query refetches
          queryClient.invalidateQueries({
            queryKey: ["ai_conversations", data.conversation_id],
          })
          queryClient.invalidateQueries({
            queryKey: ["ai_conversations"],
          })
          // Small delay then reset stream buffer
          setTimeout(() => resetStreamContent(), 100)
          break

        case "error":
          setStreaming(false)
          resetStreamContent()
          break
      }
    },
    [
      activeConversationId,
      appendStreamContent,
      addToolCall,
      completeToolCall,
      setStreaming,
      resetStreamContent,
      queryClient,
    ]
  )

  useEffect(() => {
    if (!isAuthenticated) return

    subscriptionRef.current = consumer.subscriptions.create(
      { channel: "AiChatChannel" },
      {
        received(data: unknown) {
          handleReceived(data as AiStreamMessage)
        },
      }
    )

    return () => {
      subscriptionRef.current?.unsubscribe()
      subscriptionRef.current = null
    }
  }, [isAuthenticated, handleReceived])
}
